import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ListingManagementService } from "@/services/listing/listingManagementService";
import {
  ListingQueryParams,
  CreateListingRequest,
  UpdateListingRequest,
} from "@/types/listing";

export const useListingManagement = (options?: {
  enableGetAllListings?: boolean;
}) => {
  const queryClient = useQueryClient();
  const enableGetAllListings = options?.enableGetAllListings ?? true;

  // Get all listings query (for admin) - uses admin-listings query key
  const getAllListingsQuery = useQuery({
    queryKey: ["admin-listings"],
    queryFn: () => ListingManagementService.getAllListings(),
    enabled: enableGetAllListings,
    staleTime: 0 * 60 * 1000,
    gcTime: 0 * 60 * 1000,
  });

  // Get all listings with params (for admin) - uses params in query key
  const getAllListingsWithParams = (
    params: ListingQueryParams,
    options?: { enabled?: boolean },
  ) => {
    return useQuery({
      queryKey: ["admin-listings", params],
      queryFn: () => ListingManagementService.getAllListings(params),
      enabled: options?.enabled !== false,
      staleTime: 0 * 60 * 1000,
      gcTime: 0 * 60 * 1000,
    });
  };

  // Get listing details (for admin) - returns ListingLeadbox format
  const getListingDetails = (listingId: string) => {
    return useQuery({
      queryKey: ["admin-listing-details", listingId],
      queryFn: () => ListingManagementService.getListingDetails(listingId),
      enabled: !!listingId,
      staleTime: 0 * 60 * 1000,
      gcTime: 0 * 60 * 1000,
    });
  };

  // Get cancel listing preview - async function that can be called from callbacks
  const getCancelListingPreview = async (listingId: string) => {
    return await ListingManagementService.getCancelPreview(listingId);
  };

  // Create listing mutation
  const createListingMutation = useMutation({
    mutationFn: (listingData: CreateListingRequest) =>
      ListingManagementService.createListing(listingData),
    onSuccess: (_, variables) => {
      // Invalidate and refetch listings
      queryClient.invalidateQueries({ queryKey: ["admin-listings"] });
      // Invalidate campaign queries - if campaignId is provided, invalidate all queries for that campaign
      // This includes queries with different pagination parameters
      if (variables.campaignId) {
        queryClient.invalidateQueries({
          queryKey: ["campaign", variables.campaignId],
          exact: false, // Invalidate all queries that start with ['campaign', campaignId]
        });
      }
      // Also invalidate all campaign queries to ensure consistency
      queryClient.invalidateQueries({ queryKey: ["campaign"] });
    },
  });

  // Update listing mutation
  const updateListingMutation = useMutation({
    mutationFn: (updateData: UpdateListingRequest) =>
      ListingManagementService.updateListing(updateData),
    onSuccess: (_, variables) => {
      // Invalidate specific listing and listings list
      queryClient.invalidateQueries({
        queryKey: ["admin-listing-details", variables.id],
      });
      queryClient.invalidateQueries({ queryKey: ["admin-listings"] });
    },
  });

  // Delete listing mutation
  const deleteListingMutation = useMutation({
    mutationFn: (listingId: string) =>
      ListingManagementService.deleteListing(listingId),
    onSuccess: () => {
      // Invalidate listings list
      queryClient.invalidateQueries({ queryKey: ["admin-listings"] });
    },
  });

  // Cancel listing mutation with optimistic update
  const cancelListingMutation = useMutation({
    mutationFn: (listingId: string) =>
      ListingManagementService.cancelListing(listingId),
    onMutate: async (listingId) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: ["admin-listings"] });

      // Snapshot previous value
      const previousListings = queryClient.getQueryData(["admin-listings"]);
      // Optimistically update listings
      queryClient.setQueryData(["admin-listings"], (old: any) => {
        if (!old?.listings) return old;
        return {
          ...old,
          listings: old.listings.map((listing: any) =>
            listing.id === listingId
              ? { ...listing, status: "cancelled" }
              : listing,
          ),
        };
      });

      return { previousListings };
    },
    onError: (error, listingId, context) => {
      // Rollback on error
      if (context?.previousListings) {
        queryClient.setQueryData(["admin-listings"], context.previousListings);
      }
    },
    onSuccess: () => {
      // Invalidate to sync with server
      queryClient.invalidateQueries({ queryKey: ["admin-listings"] });
      queryClient.invalidateQueries({ queryKey: ["admin-listing-details"] });
    },
  });

  // Activate reserved listing mutation with optimistic update
  const activateReservedListingMutation = useMutation({
    mutationFn: (listingId: string) =>
      ListingManagementService.activateReservedListing(listingId),
    onMutate: async (listingId) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: ["admin-listings"] });

      // Snapshot previous value
      const previousListings = queryClient.getQueryData(["admin-listings"]);

      // Optimistically update listings
      queryClient.setQueryData(["admin-listings"], (old: any) => {
        if (!old?.listings) return old;
        return {
          ...old,
          listings: old.listings.map((listing: any) =>
            listing.id === listingId
              ? {
                  ...listing,
                  status: "active",
                  winnerId: null,
                  reservedForId: null,
                }
              : listing,
          ),
        };
      });

      return { previousListings };
    },
    onError: (error, listingId, context) => {
      // Rollback on error
      if (context?.previousListings) {
        queryClient.setQueryData(["admin-listings"], context.previousListings);
      }
    },
    onSuccess: () => {
      // Invalidate to sync with server
      queryClient.invalidateQueries({ queryKey: ["admin-listings"] });
      queryClient.invalidateQueries({ queryKey: ["admin-listing-details"] });
    },
  });

  // Complete reserved listing mutation with optimistic update
  const completeReservedListingMutation = useMutation({
    mutationFn: (listingId: string) =>
      ListingManagementService.completeReservedListing(listingId),
    onMutate: async (listingId) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: ["admin-listings"] });

      // Snapshot previous value
      const previousListings = queryClient.getQueryData(["admin-listings"]);

      // Get current listing to get reservedForId for optimistic update
      const currentListings = queryClient.getQueryData([
        "admin-listings",
      ]) as any;
      const listing = currentListings?.listings?.find(
        (l: any) => l.id === listingId,
      );

      // Optimistically update listings
      queryClient.setQueryData(["admin-listings"], (old: any) => {
        if (!old?.listings) return old;
        return {
          ...old,
          listings: old.listings.map((listing: any) =>
            listing.id === listingId
              ? {
                  ...listing,
                  status: "completed",
                  winnerId: listing?.reservedForId || listing?.winnerId,
                }
              : listing,
          ),
        };
      });

      return { previousListings };
    },
    onError: (error, listingId, context) => {
      // Rollback on error
      if (context?.previousListings) {
        queryClient.setQueryData(["admin-listings"], context.previousListings);
      }
    },
    onSuccess: () => {
      // Invalidate to sync with server
      queryClient.invalidateQueries({ queryKey: ["admin-listings"] });
      queryClient.invalidateQueries({ queryKey: ["admin-listing-details"] });
    },
  });

  // Confirm buyer delivery mutation
  const confirmBuyerDeliveryMutation = useMutation({
    mutationFn: (listingId: string) =>
      ListingManagementService.confirmBuyerDelivery(listingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-listing-details"] });
      queryClient.invalidateQueries({ queryKey: ["admin-listings"] });
    },
  });

  // Save manual rating intervals (skip AutoUncle for this listing)
  const saveListingRatingIntervalsMutation = useMutation({
    mutationFn: ({
      listingId,
      intervals,
    }: {
      listingId: string;
      intervals: Array<{ label: string; upperBound: number; lowerBound: number }>;
    }) =>
      ListingManagementService.saveListingRatingIntervals(listingId, intervals),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["admin-listing-details", variables.listingId],
      });
      queryClient.invalidateQueries({ queryKey: ["admin-listings"] });
      queryClient.invalidateQueries({
        queryKey: ["listing", variables.listingId],
      });
    },
  });

  return {
    // Queries
    getAllListings: getAllListingsQuery.data,
    isLoadingListings: getAllListingsQuery.isLoading,
    listingsError: getAllListingsQuery.error,
    refetchAllListings: getAllListingsQuery.refetch,
    getAllListingsWithParams,
    getListingDetails,
    getCancelListingPreview,

    // Mutations
    createListing: createListingMutation.mutate,
    createListingAsync: createListingMutation.mutateAsync,
    isCreatingListing: createListingMutation.isPending,
    createListingError: createListingMutation.error,

    updateListing: updateListingMutation.mutate,
    updateListingAsync: updateListingMutation.mutateAsync,
    isUpdatingListing: updateListingMutation.isPending,
    updateListingError: updateListingMutation.error,

    deleteListing: deleteListingMutation.mutate,
    deleteListingAsync: deleteListingMutation.mutateAsync,
    isDeletingListing: deleteListingMutation.isPending,
    deleteListingError: deleteListingMutation.error,

    cancelListing: cancelListingMutation.mutate,
    cancelListingAsync: cancelListingMutation.mutateAsync,
    isCancellingListing: cancelListingMutation.isPending,
    cancelListingError: cancelListingMutation.error,

    activateReservedListing: activateReservedListingMutation.mutate,
    activateReservedListingAsync: activateReservedListingMutation.mutateAsync,
    isActivatingReservedListing: activateReservedListingMutation.isPending,
    activateReservedListingError: activateReservedListingMutation.error,

    completeReservedListing: completeReservedListingMutation.mutate,
    completeReservedListingAsync: completeReservedListingMutation.mutateAsync,
    isCompletingReservedListing: completeReservedListingMutation.isPending,
    completeReservedListingError: completeReservedListingMutation.error,

    confirmBuyerDelivery: confirmBuyerDeliveryMutation.mutate,
    confirmBuyerDeliveryAsync: confirmBuyerDeliveryMutation.mutateAsync,
    isConfirmingBuyerDelivery: confirmBuyerDeliveryMutation.isPending,
    confirmBuyerDeliveryError: confirmBuyerDeliveryMutation.error,

    saveListingRatingIntervals:
      saveListingRatingIntervalsMutation.mutateAsync,
    isSavingListingRatingIntervals:
      saveListingRatingIntervalsMutation.isPending,
    saveListingRatingIntervalsError:
      saveListingRatingIntervalsMutation.error,

    // Reset functions
    resetCreateListing: createListingMutation.reset,
    resetUpdateListing: updateListingMutation.reset,
    resetDeleteListing: deleteListingMutation.reset,
    resetCancelListing: cancelListingMutation.reset,
    resetActivateReservedListing: activateReservedListingMutation.reset,
    resetCompleteReservedListing: completeReservedListingMutation.reset,
  };
};
